import { HttpErrorResponse } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { MessageService } from '@corelabs/angular-messages';
import { TranslateService } from '@ngx-translate/core';
import { of, catchError, map, mergeMap, tap } from 'rxjs';
import { quizQuestionActions } from '../actions/quiz-question.actions';
import { HttpService } from '../services/http.service';
import { finalizeLoader } from '@app/shared/components';
import { ToastErrorService } from '@app/shared/services';

@Injectable()
export class QuizQuestionEffects {
    private readonly actions = inject(Actions);
    private readonly httpService = inject(HttpService);
    private readonly messageService = inject(MessageService);
    private readonly translateService = inject(TranslateService);
    private readonly toastErrorService = inject(ToastErrorService);

    getQuizQuestion$ = createEffect(() => {
        return this.actions.pipe(
            ofType(quizQuestionActions.getQuizQuestion),
            mergeMap(({ id }) => {
                return this.httpService.getQuizQuestion(id).pipe(
                    finalizeLoader('quiz-question-get'),
                    map((quizQuestion) => quizQuestionActions.getQuizQuestionSuccess({ payload: quizQuestion })),
                    catchError((response: HttpErrorResponse) => of(quizQuestionActions.getQuizQuestionError({ code: response.status, id }))),
                );
            }),
        );
    });

    createQuizQuestion$ = createEffect(() => {
        return this.actions.pipe(
            ofType(quizQuestionActions.createQuizQuestion),
            mergeMap(({ value }) => {
                return this.httpService.createQuizQuestion(value).pipe(
                    finalizeLoader('quiz-question-create'),
                    map((quizQuestion) => quizQuestionActions.createQuizQuestionSuccess({ payload: quizQuestion })),
                    tap(() => this.messageService.add(this.translateService.instant('toast.quiz-questions.created'), 'success')),
                    catchError((response: HttpErrorResponse) => {
                        this.toastErrorService.show(response, 'toast.quiz-questions.createError');
                        return of(quizQuestionActions.createQuizQuestionError({ code: response.status }));
                    }),
                );
            }),
        );
    });

    updateQuizQuestion$ = createEffect(() => {
        return this.actions.pipe(
            ofType(quizQuestionActions.updateQuizQuestion),
            mergeMap(({ id, value }) => {
                return this.httpService.updateQuizQuestion(id, value).pipe(
                    finalizeLoader('quiz-question-update'),
                    map((quizQuestion) => quizQuestionActions.updateQuizQuestionSuccess({ payload: quizQuestion })),
                    tap(() => this.messageService.add(this.translateService.instant('toast.quiz-questions.updated'), 'success')),
                    catchError((response: HttpErrorResponse) => {
                        this.toastErrorService.show(response, 'toast.quiz-questions.updateError');
                        return of(quizQuestionActions.updateQuizQuestionError({ code: response.status }));
                    }),
                );
            }),
        );
    });

    deleteQuizQuestion$ = createEffect(() => {
        return this.actions.pipe(
            ofType(quizQuestionActions.deleteQuizQuestion),
            mergeMap(({ id }) => {
                return this.httpService.deleteQuizQuestion(id).pipe(
                    finalizeLoader('quiz-question-delete'),
                    map(() => quizQuestionActions.deleteQuizQuestionSuccess({ id })),
                    tap(() => this.messageService.add(this.translateService.instant('toast.quiz-questions.deleted'), 'success')),
                    catchError((response: HttpErrorResponse) => {
                        this.toastErrorService.show(response, 'toast.quiz-questions.deleteError');
                        return of(quizQuestionActions.deleteQuizQuestionError({ code: response.status }));
                    }),
                );
            }),
        );
    });
}
